import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import styled from "styled-components";
import {
  Modal,
  ModalBackground,
  ModalContent,
  ModalClose,
  ModalHeading,
} from "./library/Modal";
import Button from "./library/Button";
import ButtonGroup from "./library/ButtonGroup";
import {
  FormError,
  SubmitButton,
  TextInputField,
  RadioFieldGroup,
  RadioField,
} from "./library/Form";
import InviteUser from "../apiResources/InviteUser";
import { useCurrentUser } from "../api/user";
import { useQueryClient } from "react-query";

type InviteTeamMemberModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: "member" | "admin";
};

const InviteTeamMemberModal = (props: InviteTeamMemberModalProps) => {
  const { isOpen, onDismiss, onSuccess } = props;
  const [formError, setFormError] = useState("");
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();

  const inviteTeamMember = async (values: FormValues) => {
    try {
      const invite = new InviteUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        admin: values.role === "admin",
        invitedById: currentUser.id,
        companyId: currentUser.companyId,
        type: currentUser.isRecruiter ? "RecruiterUser" : "EmployerUser",
      });

      const success = invite.save();

      if (success) {
        queryClient.invalidateQueries("teamMembers");
        onSuccess();
      }
    } catch (err) {
      setFormError("😬 Uh oh, something went wrong there!");
      Sentry.captureException(err);
    }
  };

  return (
    <Modal id="invite-member-modal" isOpen={isOpen} onDismiss={onDismiss}>
      <ModalBackground onClick={onDismiss} />
      <ModalContent>
        <ModalHeading>Invite Team Member</ModalHeading>

        {formError && <FormError>{formError}</FormError>}

        <Formik<FormValues>
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            role: "member",
          }}
          onSubmit={inviteTeamMember}
          validationSchema={Yup.object({
            firstName: Yup.string().required("Please enter a first name"),
            lastName: Yup.string().required("Please enter a last name"),
            email: Yup.string()
              .email("Please enter an email")
              .required("Please enter an email"),
          })}
        >
          <SpacedForm>
            <FieldsLayout>
              <TextInputField name="firstName" label="First Name" />
              <TextInputField name="lastName" label="Last Name" />
              <TextInputField type="email" name="email" label="Email" />

              <RadioFieldGroup label="User Role" name="role">
                <RadioField name="role" value="member">
                  Member
                </RadioField>
                <RadioField name="role" value="admin">
                  Admin
                </RadioField>
              </RadioFieldGroup>
            </FieldsLayout>
            <ButtonGroup>
              <SubmitButton submittingText="Sending...">Invite</SubmitButton>
              <Button tertiary onClick={onDismiss}>
                Cancel
              </Button>
            </ButtonGroup>
          </SpacedForm>
        </Formik>

        <ModalClose onClick={onDismiss} aria-label="Close candidate modal" />
      </ModalContent>
    </Modal>
  );
};

const SpacedForm = styled(Form)`
  margin-top: 2rem;
`;

const FieldsLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1.5rem;

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export default InviteTeamMemberModal;
